import { useState, useEffect, useCallback, useRef } from 'react';

const CONSENT_KEY = 'cookie_consent';
const CONSENT_VERSION = '1.0';

interface CookieConsent {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

function getStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent: CookieConsent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: consent }));
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(getStoredConsent);

  useEffect(() => {
    const handler = (e: Event) => setConsent((e as CustomEvent).detail);
    window.addEventListener('cookie-consent-changed', handler);
    return () => window.removeEventListener('cookie-consent-changed', handler);
  }, []);

  return consent;
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const prefsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) setVisible(true);
  }, []);

  const accept = useCallback((analyticsVal: boolean, marketingVal: boolean) => {
    const consent: CookieConsent = {
      essential: true,
      analytics: analyticsVal,
      marketing: marketingVal,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    saveConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showPrefs) setShowPrefs(false);
    }
  }, [showPrefs]);

  useEffect(() => {
    if (showPrefs && prefsRef.current) {
      const first = prefsRef.current.querySelector('button, input, [tabindex]') as HTMLElement;
      first?.focus();
    }
  }, [showPrefs]);

  if (!visible) return null;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const transition = prefersReduced ? '' : 'transition-all duration-300 ease-out';

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie consent"
      aria-modal={showPrefs}
      onKeyDown={handleKeyDown}
      className={`fixed bottom-0 left-0 right-0 z-[60] ${transition}`}
    >
      {/* Backdrop for preferences panel */}
      {showPrefs && (
        <div
          className="fixed inset-0 bg-black/50 z-[59]"
          onClick={() => setShowPrefs(false)}
          aria-hidden="true"
        />
      )}

      <div className="relative z-[60] border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-md">
        {/* Preferences panel */}
        {showPrefs && (
          <div
            ref={prefsRef}
            className="mx-auto max-w-4xl px-6 pt-6 pb-2"
            role="region"
            aria-label="Cookie preferences"
          >
            <h3 className="text-sm font-semibold text-zinc-100 mb-4">Manage Cookie Preferences</h3>
            <div className="space-y-3">
              {/* Essential */}
              <div className="flex items-center justify-between rounded-lg bg-zinc-900/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-zinc-200">Essential Cookies</p>
                  <p className="text-xs text-zinc-500">Required for the site to function. Cannot be disabled.</p>
                </div>
                <div className="flex h-6 w-10 items-center rounded-full bg-emerald-600 px-0.5 cursor-not-allowed" aria-label="Essential cookies always enabled">
                  <div className="h-5 w-5 rounded-full bg-white translate-x-4" />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between rounded-lg bg-zinc-900/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-zinc-200">Analytics Cookies</p>
                  <p className="text-xs text-zinc-500">Help us understand how visitors use our site.</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  role="switch"
                  aria-checked={analytics}
                  aria-label="Toggle analytics cookies"
                  className={`flex h-6 w-10 items-center rounded-full px-0.5 ${transition} ${analytics ? 'bg-emerald-600' : 'bg-zinc-700'}`}
                >
                  <div className={`h-5 w-5 rounded-full bg-white ${transition} ${analytics ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between rounded-lg bg-zinc-900/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-zinc-200">Marketing Cookies</p>
                  <p className="text-xs text-zinc-500">Used to deliver relevant ads and track campaigns.</p>
                </div>
                <button
                  onClick={() => setMarketing(!marketing)}
                  role="switch"
                  aria-checked={marketing}
                  aria-label="Toggle marketing cookies"
                  className={`flex h-6 w-10 items-center rounded-full px-0.5 ${transition} ${marketing ? 'bg-emerald-600' : 'bg-zinc-700'}`}
                >
                  <div className={`h-5 w-5 rounded-full bg-white ${transition} ${marketing ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => accept(analytics, marketing)}
                className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}

        {/* Main banner */}
        <div className="mx-auto max-w-4xl px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-zinc-400">
              We use cookies to improve your experience.{' '}
              <a href="/cookies" className="text-zinc-300 underline underline-offset-2 hover:text-white">
                Learn more
              </a>
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => setShowPrefs(!showPrefs)}
                className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-expanded={showPrefs}
                aria-controls="cookie-preferences"
              >
                Manage
              </button>
              <button
                onClick={() => accept(false, false)}
                className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                Reject Non-Essential
              </button>
              <button
                onClick={() => accept(true, true)}
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-zinc-900 hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
